import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Collections } from "@graylog/enterprise-api";
import i18n from "i18n";
import UserNotification from "util/UserNotification";
import { COLLECTIONS_QUERY_KEY } from "collections/hooks/useCollections";
const deleteCollection = async ({ id }) => Collections.remove(id);
const bulkDeleteCollections = async ({ entityIds }) => Collections.bulkDelete({ entity_ids: entityIds });
const createCollection = async (collectionRequest) => {
  var _a;
  const { share_request, ...rest } = collectionRequest;
  return Collections.create({
    entity: {
      title: rest == null ? void 0 : rest.title,
      description: rest == null ? void 0 : rest.description,
      id: rest == null ? void 0 : rest.id,
      entities: rest == null ? void 0 : rest.entities
    },
    share_request: {
      selected_grantee_capabilities: (_a = share_request == null ? void 0 : share_request.selected_grantee_capabilities) == null ? void 0 : _a.toJS(),
      selected_collections: share_request == null ? void 0 : share_request.selected_collections
    }
  });
};
const updateCollection = async (newCollection) => Collections.update(newCollection.id, {
  title: newCollection == null ? void 0 : newCollection.title,
  description: newCollection == null ? void 0 : newCollection.description
});
const updateCollectionsForEntity = async ({
  entityId,
  payload
}) => Collections.updateCollections(entityId, { add_to: payload.addTo, remove_from: payload.removeFrom });
const useCollectionMutation = () => {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: deleteCollection,
    onError: (errorThrown) => {
      UserNotification.error(
        i18n.t("notifications.collection_delete_failed", { ns: "enterprise_collections", error: errorThrown }),
        i18n.t("notifications.collection_delete_failed_title", { ns: "enterprise_collections" })
      );
    },
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.collection_delete_success", { ns: "enterprise_collections" }),
        i18n.t("notifications.success_title", { ns: "enterprise_collections" })
      );
      queryClient.invalidateQueries({ queryKey: [COLLECTIONS_QUERY_KEY], type: "active" });
    }
  });
  const bulkRemove = useMutation({
    mutationFn: bulkDeleteCollections
  });
  const updateEntityCollectionsMutation = useMutation({
    mutationFn: updateCollectionsForEntity,
    onError: (errorThrown) => {
      UserNotification.error(
        i18n.t("notifications.collection_entities_update_failed", { ns: "enterprise_collections", error: errorThrown }),
        i18n.t("notifications.collection_entities_update_failed_title", { ns: "enterprise_collections" })
      );
    },
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.collection_entities_update_success", { ns: "enterprise_collections" }),
        i18n.t("notifications.success_title", { ns: "enterprise_collections" })
      );
      queryClient.refetchQueries({ queryKey: [COLLECTIONS_QUERY_KEY, "entity"], type: "active" });
    }
  });
  const create = useMutation({
    mutationFn: createCollection,
    onError: (errorThrown) => {
      UserNotification.error(
        i18n.t("notifications.collection_create_failed", { ns: "enterprise_collections", error: errorThrown }),
        i18n.t("notifications.collection_create_failed_title", { ns: "enterprise_collections" })
      );
    },
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.collection_create_success", { ns: "enterprise_collections" }),
        i18n.t("notifications.success_title", { ns: "enterprise_collections" })
      );
      queryClient.refetchQueries({ queryKey: [COLLECTIONS_QUERY_KEY], type: "active" });
    }
  });
  const update = useMutation({
    mutationFn: updateCollection,
    onError: (errorThrown) => {
      UserNotification.error(
        i18n.t("notifications.collection_update_failed", { ns: "enterprise_collections", error: errorThrown }),
        i18n.t("notifications.collection_update_failed_title", { ns: "enterprise_collections" })
      );
    },
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.collection_update_success", { ns: "enterprise_collections" }),
        i18n.t("notifications.success_title", { ns: "enterprise_collections" })
      );
      queryClient.refetchQueries({ queryKey: [COLLECTIONS_QUERY_KEY], type: "active" });
    }
  });
  return {
    deleteCollection: remove.mutateAsync,
    bulkDeleteCollectionsMutation: bulkRemove,
    createCollection: create.mutateAsync,
    updateCollection: update.mutateAsync,
    updateEntityCollectionsMutation: updateEntityCollectionsMutation.mutateAsync
  };
};
export default useCollectionMutation;
